import { useMemo } from 'react';
import { allocateSlug, appGeneratePath, type RoutePath } from '@/shared/lib';
import { useTablePresets, type TablePreset } from './useTablePresets';

/**
 * Шаблон адреса раздела закреплённого пресета — единственное место, где он задан.
 * Необязательный сегмент записи входит в шаблон: работа с записью не выводит из раздела,
 * поэтому потребители строят и адрес списка (`id: null`), и адрес записи отсюда же.
 */
export const PINNED_PRESET_ROUTE_PATH = '/preset/:slug/:id?' satisfies RoutePath;

/** Слаг пресета, из названия которого не выходит ни одного адресного символа */
const FALLBACK_SLUG = 'preset';

/** Закреплённый пресет вместе с адресом его раздела */
export type PinnedPresetRoute = {
  /** Сам закреплённый пресет */
  preset: TablePreset;
  /** Слаг пресета — транслитерация названия, различающаяся у закреплённых пресетов */
  slug: string;
  /** Полный адрес раздела пресета */
  path: string;
};

/**
 * Строит адреса разделов закреплённых пресетов.
 *
 * Слаг считается от текущего названия, поэтому переименование меняет и адрес; совпадения
 * названий разводит {@link allocateSlug}.
 */
const buildPinnedPresetRoutes = (presets: TablePreset[]): PinnedPresetRoute[] => {
  const usedSlugs = new Set<string>();
  return presets
    .filter((preset) => preset.pinned)
    .map((preset) => {
      const slug = allocateSlug(preset.name, usedSlugs, FALLBACK_SLUG);
      return { preset, slug, path: appGeneratePath(PINNED_PRESET_ROUTE_PATH, { slug, id: null }) };
    });
};

/**
 * Хук адресов закреплённых пресетов таблицы: по пункту меню на каждый и по адресу,
 * который открывает список с применённым пресетом.
 *
 * @param tableName - Имя таблицы (например, `'dialogsListV2'`).
 */
export const usePinnedPresetRoutes = (tableName: string): PinnedPresetRoute[] => {
  const { presets } = useTablePresets(tableName);
  return useMemo(() => buildPinnedPresetRoutes(presets), [presets]);
};
